import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuGlobe, LuSearch } from "react-icons/lu";

const IPAPI_URL = "https://ipapi.co/json/";
const FORCE_ENGLISH_COUNTRY_CODES = new Set(["IN", "AE"]);

/** Persisted Google combo value: `""` = English (original). Only written when the user picks from the dropdown. */
const TRANSLATE_LANG_STORAGE_KEY = "upforex.cabinet.translateLang";

/** Label for the original page language (Google omits `en` when pageLanguage is `en`). */
const ORIGINAL_PAGE_LABEL = "English";

function getPersistedTranslateLang() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const v = localStorage.getItem(TRANSLATE_LANG_STORAGE_KEY);
    return v;
  } catch {
    return null;
  }
}

function setPersistedTranslateLang(value) {
  try {
    localStorage.setItem(TRANSLATE_LANG_STORAGE_KEY, value);
  } catch {
    /* quota / private mode */
  }
}

function clearPersistedTranslateLang() {
  try {
    localStorage.removeItem(TRANSLATE_LANG_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function ensureTranslateHost() {
  let host = document.getElementById("google_translate_element");
  if (!host) {
    host = document.createElement("div");
    host.id = "google_translate_element";
    host.setAttribute("aria-hidden", "true");
    host.style.position = "absolute";
    host.style.left = "-9999px";
    host.style.width = "1px";
    host.style.height = "1px";
    host.style.overflow = "hidden";
    document.body.appendChild(host);
  }
  return host;
}

function getCombo() {
  return (
    document.getElementById("google_translate_element")?.querySelector(".goog-te-combo") ||
    document.querySelector(".goog-te-combo")
  );
}

let translateLoaderStarted = false;
let sharedReady = false;
let sharedItems = [];
let sharedValue = "";
let sharedInstance = null;
const translateListeners = new Set();

function notifyTranslateListeners() {
  translateListeners.forEach((listener) => listener());
}

function publishTranslateCombo(select) {
  const hasOriginalOption = Array.from(select.options).some((o) => o.value === "");
  if (!hasOriginalOption) {
    const opt = document.createElement("option");
    opt.value = "";
    opt.text = ORIGINAL_PAGE_LABEL;
    select.insertBefore(opt, select.firstChild);
  }

  sharedItems = Array.from(select.options).map((opt) => ({
    value: opt.value,
    label: opt.value === "" ? ORIGINAL_PAGE_LABEL : opt.text,
  }));
  sharedValue = select.value;
  sharedReady = true;
  notifyTranslateListeners();
}

function initTranslateElement() {
  ensureTranslateHost();
  if (getCombo()?.options?.length) return;
  if (!window.google?.translate?.TranslateElement) return;

  const host = ensureTranslateHost();
  if (host.querySelector(".goog-te-combo")) host.innerHTML = "";

  try {
    sharedInstance = new window.google.translate.TranslateElement(
      { pageLanguage: "en", autoDisplay: false },
      "google_translate_element",
    );
  } catch (_) {
    /* Google may reject a second init until the host is cleared */
  }
}

function startTranslateLoader() {
  if (translateLoaderStarted) return;
  translateLoaderStarted = true;

  window.googleTranslateElementInit = initTranslateElement;

  const existing = document.querySelector(
    'script[src*="translate.google.com/translate_a/element.js"]',
  );
  if (!existing) {
    const script = document.createElement("script");
    script.src =
      "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    document.body.appendChild(script);
  } else if (window.google?.translate?.TranslateElement) {
    initTranslateElement();
  }

  let ticks = 0;
  const poll = setInterval(() => {
    const select = getCombo();
    if (select?.options?.length) {
      publishTranslateCombo(select);
      clearInterval(poll);
      return;
    }

    ticks += 1;
    if (ticks === 1 || ticks % 20 === 0) initTranslateElement();
  }, 200);
}

function clearGoogleTranslateCookiesOnly() {
  const expire = "expires=Thu, 01 Jan 1970 00:00:01 GMT";
  const paths = ["/", ""];
  const host = window.location.hostname;
  const domains = [null, host, host ? `.${host}` : null].filter(Boolean);

  for (const path of paths) {
    const p = path || "/";
    document.cookie = `googtrans=;path=${p};${expire}`;
    for (const domain of domains) {
      document.cookie = `googtrans=;path=${p};domain=${domain};${expire}`;
    }
  }
}

/** Walks minified TranslateElement internals to call `restore()` when present (no page reload). */
function deepTryCallRestore(instance) {
  if (!instance || typeof instance !== "object") return false;
  const seen = new WeakSet();
  const walk = (obj, depth) => {
    if (!obj || typeof obj !== "object" || depth > 12) return false;
    if (seen.has(obj)) return false;
    seen.add(obj);
    try {
      if (typeof obj.restore === "function") {
        obj.restore();
        return true;
      }
    } catch (_) {
      /* ignore */
    }
    for (const key of Object.keys(obj)) {
      try {
        if (walk(obj[key], depth + 1)) return true;
      } catch (_) {
        /* ignore */
      }
    }
    return false;
  };
  return walk(instance, 0);
}

function syncComboToOriginalLanguage() {
  const select = getCombo();
  if (!select) return;
  const idx = Array.from(select.options).findIndex((o) => o.value === "");
  if (idx >= 0) select.selectedIndex = idx;
  select.value = "";
  select.dispatchEvent(new Event("change", { bubbles: true }));
  try {
    if (typeof select.onchange === "function") select.onchange();
  } catch (_) {
    /* ignore */
  }
}

function restoreTranslatedPageToOriginal(translateElementInstance) {
  syncComboToOriginalLanguage();
  if (translateElementInstance) {
    deepTryCallRestore(translateElementInstance);
  }
  clearGoogleTranslateCookiesOnly();
  document.body.classList.remove("translated-ltr", "translated-rtl");
  document.documentElement.classList.remove("translated-ltr", "translated-rtl");
}

function parseIpapiLanguageList(languagesField) {
  if (!languagesField || typeof languagesField !== "string") return [];
  return languagesField.split(",").map((s) => s.trim()).filter(Boolean);
}

function isEnglishVariant(bcp47) {
  const primary = bcp47.split("-")[0].toLowerCase();
  return primary === "en";
}

function candidateCodesForTag(tag) {
  const t = tag.trim();
  if (!t) return [];
  const parts = t.split("-");
  const primary = parts[0].toLowerCase();
  const out = [t, primary];
  if (parts.length >= 2) {
    const region = parts[1];
    out.push(`${primary}-${region.toUpperCase()}`, `${primary}-${region.toLowerCase()}`);
  }
  return [...new Set(out.filter(Boolean))];
}

function resolveOptionValue(select, candidate) {
  for (const o of select.options) {
    if (!o.value) continue;
    if (o.value === candidate) return o.value;
    if (o.value.toLowerCase() === candidate.toLowerCase()) return o.value;
  }
  return null;
}

/** Picks the first ipapi language tag that exists in Google's combo (page is English). */
function findFirstSupportedLanguage(select, preferredBcp47Tags) {
  for (const tag of preferredBcp47Tags) {
    if (isEnglishVariant(tag)) continue;
    for (const c of candidateCodesForTag(tag)) {
      const v = resolveOptionValue(select, c);
      if (v) return v;
    }
  }
  return null;
}

const GoogleTranslate = ({ compact = false }) => {
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);
  const translateElementRef = useRef(null);
  const ipapiTagsRef = useRef(null);
  const ipapiCountryCodeRef = useRef(null);
  const autoAppliedRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [ready, setReady] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedValue, setSelectedValue] = useState("");

  const applyInitialOrPersistedLanguage = () => {
    const combo = getCombo();
    if (!combo?.options?.length) return;
    if (autoAppliedRef.current) return;

    const saved = getPersistedTranslateLang();
    if (saved !== null) {
      if (saved === "") {
        autoAppliedRef.current = true;
        restoreTranslatedPageToOriginal(translateElementRef.current);
        setSelectedValue("");
        return;
      }
      const valid = Array.from(combo.options).some((o) => o.value === saved);
      if (valid) {
        autoAppliedRef.current = true;
        combo.value = saved;
        combo.dispatchEvent(new Event("change", { bubbles: true }));
        setSelectedValue(saved);
        return;
      }
      clearPersistedTranslateLang();
    }

    if (ipapiTagsRef.current === null) return;

    autoAppliedRef.current = true;

    const geoCountryCode = String(ipapiCountryCodeRef.current || "").toUpperCase();
    if (FORCE_ENGLISH_COUNTRY_CODES.has(geoCountryCode)) {
      restoreTranslatedPageToOriginal(translateElementRef.current);
      setSelectedValue("");
      return;
    }

    const geoPick = findFirstSupportedLanguage(combo, ipapiTagsRef.current);
    if (geoPick) {
      combo.value = geoPick;
      combo.dispatchEvent(new Event("change"));
      setSelectedValue(geoPick);
      return;
    }

    const browserLang = navigator.language?.split("-")[0] || "en";
    if (browserLang !== "en" && combo.querySelector(`option[value="${browserLang}"]`)) {
      combo.value = browserLang;
      combo.dispatchEvent(new Event("change"));
      setSelectedValue(browserLang);
    }
  };

  const applyLanguage = (value) => {
    if (value === "") {
      setOpen(false);
      setPersistedTranslateLang("");
      restoreTranslatedPageToOriginal(translateElementRef.current);
      setSelectedValue("");
      return;
    }
    const select = getCombo();
    if (!select) return;
    setPersistedTranslateLang(value);
    select.value = value;
    select.dispatchEvent(new Event("change", { bubbles: true }));
    setSelectedValue(value);
    setOpen(false);
  };

  useEffect(() => {
    let alive = true;
    const finish = (tags, countryCode = "") => {
      if (!alive) return;
      ipapiTagsRef.current = tags;
      ipapiCountryCodeRef.current = countryCode;
      applyInitialOrPersistedLanguage();
    };

    const timeoutId = window.setTimeout(() => finish([], ""), 8000);

    fetch(IPAPI_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("ipapi"))))
      .then((data) => {
        window.clearTimeout(timeoutId);
        finish(
          parseIpapiLanguageList(data?.languages),
          String(data?.country_code || data?.country || "").toUpperCase(),
        );
      })
      .catch(() => {
        window.clearTimeout(timeoutId);
        finish([], "");
      });

    return () => {
      alive = false;
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    startTranslateLoader();

    const syncLanguages = () => {
      if (!sharedReady) return;
      translateElementRef.current = sharedInstance;
      setItems(sharedItems);
      setSelectedValue(getCombo()?.value || sharedValue);
      setReady(true);
      applyInitialOrPersistedLanguage();
    };

    translateListeners.add(syncLanguages);
    syncLanguages();

    return () => {
      translateListeners.delete(syncLanguages);
    };
  }, []);

  useEffect(() => {
    const select = getCombo();
    if (!select) return;
    const onChange = () => setSelectedValue(select.value);
    select.addEventListener("change", onChange);
    return () => select.removeEventListener("change", onChange);
  }, [ready]);

  const placeMenu = () => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const margin = 8;
    const width = Math.min(280, window.innerWidth - margin * 2);
    const left = Math.min(
      Math.max(margin, rect.right - width),
      window.innerWidth - width - margin,
    );
    setMenuStyle({
      position: "fixed",
      top: rect.bottom + 8,
      left,
      width,
      zIndex: 1100,
    });
  };

  useEffect(() => {
    if (!open) return;
    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const target = e.target;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      return;
    }
    const id = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const selectedRow = items.find((i) => i.value === selectedValue);
  const triggerLabel = selectedRow?.label || "Language";

  const sortedItems = [...items].sort((a, b) => {
    if (a.value === "") return -1;
    if (b.value === "") return 1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
  });

  const search = searchQuery.trim().toLowerCase();
  const filteredItems = search
    ? sortedItems.filter((row) => {
        const label = row.label.toLowerCase();
        const val = row.value.toLowerCase();
        return label.includes(search) || val.includes(search);
      })
    : sortedItems;

  return (
    <div className="relative shrink-0 z-20" ref={rootRef}>
      {/* Google Translate skips elements with notranslate — keep language names as Google provides */}
      <div className="notranslate" translate="no">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => {
            if (!open) placeMenu();
            setOpen((v) => !v);
          }}
          className={
            compact
              ? "inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-[#f0f2f5] text-[#2F2B3D] transition hover:bg-[#e4e7ec]"
              : "inline-flex max-w-[200px] cursor-pointer items-center gap-2 rounded-full bg-[#f0f2f5] px-3 py-1.5 text-sm font-medium text-[#2F2B3D] transition hover:bg-[#e4e7ec]"
          }
          aria-haspopup="listbox"
          aria-expanded={open}
          title={triggerLabel}
        >
          <LuGlobe className="h-4 w-4 shrink-0 text-[#2F2B3D]" aria-hidden />
          {!compact && <span className="truncate text-left">{triggerLabel}</span>}
        </button>

        {open &&
          menuStyle &&
          createPortal(
            <div
              ref={menuRef}
              style={menuStyle}
              className="notranslate flex max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-2xl border border-black/5 bg-white shadow-lg shadow-black/10"
              translate="no"
              role="presentation"
            >
              <div className="shrink-0 border-b border-black/5 px-3 pb-2 pt-3">
                <label className="sr-only" htmlFor="google-translate-lang-search">
                  Search languages
                </label>
                <div className="relative">
                  <LuSearch
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2F2B3D]/45"
                    aria-hidden
                  />
                  <input
                    ref={searchInputRef}
                    id="google-translate-lang-search"
                    type="search"
                    autoComplete="off"
                    placeholder="Search languages..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-xl border border-black/10 bg-[#f5f6f8] py-2 pl-9 pr-3 text-sm text-[#2F2B3D] outline-none ring-0 placeholder:text-[#2F2B3D]/40 focus:border-[#2F2B3D]/20 focus:bg-white"
                  />
                </div>
              </div>

              <ul
                className="max-h-[min(280px,45vh)] list-none overflow-y-auto overscroll-contain py-1"
                role="listbox"
                aria-label="Languages"
              >
                {!ready ? (
                  <li className="px-5 py-6 text-center text-sm text-[#2F2B3D]/55">
                    Loading languages...
                  </li>
                ) : filteredItems.length === 0 ? (
                  <li className="px-5 py-6 text-center text-sm text-[#2F2B3D]/55">
                    No languages match your search
                  </li>
                ) : (
                  filteredItems.map((row) => (
                    <li key={row.value === "" ? "__original__" : row.value} role="none">
                      <button
                        type="button"
                        role="option"
                        aria-selected={row.value === selectedValue}
                        onClick={() => applyLanguage(row.value)}
                        className={`w-full px-5 py-3 text-left text-sm text-[#2F2B3D] transition hover:bg-[#f5f6f8] ${
                          row.value === selectedValue ? "bg-[#f0f2f5]/80" : ""
                        }`}
                      >
                        {row.label}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

export default GoogleTranslate;
